import React, {useState} from 'react';
import {useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {GridRowModel} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import {Panel} from '../Panel/Panel';
import {
  DELETE_REVIEW,
  GET_ACCOUNT_REVIEWS,
} from '../AccountOverview/accountOverviewQueries';
import AccountReviewsGrid from '../AccountReviewsGrid/AccountReviewsGrid';
import {useAccountContext} from '../../contexts/AccountContext';
import {themeColors} from '../../theme/Theme';
import Loading from '../Loading/Loading';
import {
  AccountReviewsVariables,
  AccountReviews_reviews,
} from '../../schema-types/AccountReviews';
import {isDevOrTester, useDjangoContext} from '../../contexts/useAuthContext';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {DeleteReview, DeleteReviewVariables} from '../../schema-types/DeleteReview';
import {ConfirmationModal} from '../Common/ConfirmationModal';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';

function ReviewsPanel() {
  const {account, accountId, accountLoading} = useAccountContext();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();
  const [deletionId, setDeletionId] = useState<string | undefined>();
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const {djangoUser} = useDjangoContext();
  const [deleteReview, {loading: deleteLoading}] = useMutation<
    DeleteReview,
    DeleteReviewVariables
  >(DELETE_REVIEW, {
    // eslint-disable-next-line consistent-return
    onCompleted: ({deleteReview: deleteReviewData}) => {
      if (!deleteReviewData?.ok) {
        return enqueueSnackbar('Unable to delete the review.', {variant: 'error'});
      }
    },
    onError: (err) => {
      enqueueSnackbar(`Unable to delete the review. ${err.message}`, {
        variant: 'error',
        autoHideDuration: null,
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_ACCOUNT_REVIEWS,
        variables: {
          accountId: account?.id || accountId!,
          idType: 'id',
          onlyCurrent: true,
          curoSync: true,
        } as AccountReviewsVariables,
      },
    ],
  });

  const createActiveReview = async () => {
    navigate({pathname: `/account/${accountId!}/precursor`});
  };

  const deleteActiveReview = async (review: AccountReviews_reviews) => {
    if (!review) {
      return;
    }

    /**
     * having a CuroID means a review has been submitted already
     */
    if (review.curoId) {
      if (isDevOrTester(djangoUser)) {
        /**
         * By setting an ID to be deleted, we trigger the modal to pop up
         * where the dev or tester will confirm the permanent deletion
         */
        setDeletionId(review.id);
      } else {
        enqueueSnackbar('You can only delete an active review.', {
          variant: 'error',
          autoHideDuration: null,
        });
      }
      return;
    }

    /**
     * Reviews that are not in Curo can only be soft deleted
     */
    await deleteReview({
      variables: {
        reviewId: review.id,
      },
    }).then(() =>
      enqueueSnackbar('The selected review has been deleted.', {
        variant: 'success',
      })
    );
  };

  const onSelected = (r: GridRowModel) => setSelectedReviewId(r?.id);
  // const dummyReviewForQuickNewRecommend = useMemo(() => {
  //   if (accountReviews?.reviews && accountReviews?.reviews.length) {
  //     const review = accountReviews?.reviews.find((x) => {
  //       if (x.stage === ReviewStage.BOOKED) {
  //         return false;
  //       }
  //       if (x.stage === ReviewStage.NEED_BOOKING) {
  //         return false;
  //       }
  //       return true;
  //     });
  //     if (review) {
  //       return review;
  //     }
  //   }
  //   return null;
  // }, [accountReviews?.reviews]);

  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_ehons_notes_goals]}
      showErrorIfDenied
    >
      <Panel color={themeColors.blue} title={`${account?.name}'s Reviews`}>
        {accountLoading ? (
          <Loading />
        ) : (
          <AccountReviewsGrid
            deleteLoading={deleteLoading}
            createReview={createActiveReview}
            createReviewLoading={false}
            deleteReview={deleteActiveReview}
            onSelected={onSelected}
          />
        )}
      </Panel>
      {!!deletionId && (
        <ConfirmationModal
          open={!!deletionId}
          handleClose={() => setDeletionId(undefined)}
          loading={deleteLoading}
          title="Permanent Deletion"
          message="Are you sure you want to delete this review? The review journey has been started already."
          confirmAction={async () => {
            await deleteReview({
              variables: {
                reviewId: deletionId,
              },
            }).then((x) => {
              if (x.data?.deleteReview?.ok) {
                enqueueSnackbar('The selected review has been deleted.', {
                  variant: 'success',
                });
              } else {
                enqueueSnackbar('There was an error and the review was not deleted.', {
                  variant: 'error',
                  autoHideDuration: null,
                });
              }

              setDeletionId(undefined);
            });
          }}
        />
      )}
    </FeatureBoundary>
  );
}

export default ReviewsPanel;
